import { Component, signal, computed, DestroyRef, inject, NgZone, ChangeDetectorRef, HostListener } from '@angular/core';
import { Commands } from '../../services/commands/commands';
import { Events } from '../../services/events/events';
import { DICTIONARY } from './dictionary.constants';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EndGame } from '../end-game/end-game';

@Component({
  selector: 'app-prompter-board',
  imports: [EndGame],
  templateUrl: './prompter-board.html',
  styleUrl: './prompter-board.css'
})
export class PrompterBoard {

  // PROPERTIES

  private destroyRef = inject(DestroyRef);
  private timer: number | null = null;
  private sound: HTMLAudioElement = new Audio("assets/sound.mp3");

  // SIGNALS

  private time = signal<number>(60000); // ms
  private is_round_doubled = signal<boolean>(false);

  private words = signal<{ // stacks
    single: string[],
    double: string[]
  }>({
    single: [],
    double: []
  });

  protected passes = signal<number>(3);
  protected doubles = signal<number>(2);
  protected points = signal<number>(0);
  protected is_sound_on = signal<boolean>(false);
  protected is_round_active = signal<boolean>(false);

  // COMPUTED SIGNALS

  protected is_game_active = computed<boolean>(
    () => this.time() > 0
  );

  protected rounded_time = computed<number>(() =>
    Math.ceil(this.time() / 1000)
  );

  protected current_word = computed<string>(() =>

    this.is_round_doubled()
      ? this.words().double[this.words().double.length - 1]
      : this.words().single[this.words().single.length - 1]

  );

  // EVENT LISTENERS

  private roundStartListener(): void{

    this.events.onRoundStart()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {

          const { sync_time, is_double_mode } = data;

          // handle timer
          this.startTimer(sync_time);

          // handle doubles
          if (is_double_mode){

            this.doubles.update(value => value - 1);
            this.is_round_doubled.set(true);

          }

          else this.is_round_doubled.set(false);

          // handle round status
          this.is_round_active.set(true);

        }
      })

  }

  private roundEndListener(): void{
   
    this.events.onRoundEnd()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {

          this.stopTimer();
          this.is_sound_on() && this.sound.play();
          this.is_round_active.set(false);
          this.time.set(data.time);

          // move the last element if front of the array instead of removing it
          // to prevent the case where all words are consumed (see the first words again is better than seeing nothing)

          if (this.is_round_doubled())

            this.words.update(value => ({
              ...value,
              double: [value.double.at(-1)!, ...value.double.slice(0, -1)]
            }));

          else
            
            this.words.update(value => ({
              ...value,
              single: [value.single.at(-1)!, ...value.single.slice(0, -1)]
            }));

        }
      });

  }

  private WordPassedListener(): void{

    this.events.onWordPassed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {
          this.passes.update(value => value - 1);
        }
      })

  }

  private pointsUpdatedListener(): void{

    this.events.onPointsUpdated()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {

          const points = data.points;
          this.points.set(points);
          
        }
      })

  }

  private playerLeftListener(): void{

    this.events.onPlayerLeft()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {

          // we cannot rely on is_game_active signal
          // because of possible small sync gap between the time of the 2 players
          if (data.is_game_ended) return;

          alert("The connection to a player is lost. The match has ended.");
          this.exitBoard();

        }
      });

  }

  // TIME MANAGEMENT

  private startTimer(sync_time: number): void{

    // No signal should be used here, as all the logic needs to be synchronous.
    // In fact, the countodown function is brittle, as it is called around every 16 ms.

    let curr_time = sync_time;
    let curr_seconds = this.rounded_time();

    const countdown = () => {

      // base case
      if (curr_seconds === 0) return;

      // new time calculation
      // Math.max is needed in case the expected round trip time is bigger then the expected one
      const time = Date.now();
      this.time.update(value => value - Math.max(0, time - curr_time));
      curr_time = time;

      // new seconds calculation
      const seconds = this.rounded_time();

      if (seconds < curr_seconds){

        curr_seconds = seconds;
        this.change_detector_ref.detectChanges();

      }

      // recursion
      this.timer = requestAnimationFrame(countdown);

    };

    // run timer outside angular to improve performances
    this.ng_zone.runOutsideAngular(() => {
      this.timer = requestAnimationFrame(countdown);
    });

  }

  private stopTimer(): void{

    this.timer && cancelAnimationFrame(this.timer);
    this.timer = null;

  }

  // WORDS MANAGEMENT

  private getWords(): void{

    const getArrayRandomElements = (array: string[], count: number) => {

      // THIS ALGHORITM WORKS IN O(COUNT)

      const selected_elements = [];

      for (let i = 0; i < count; i++){

        // create random array
        const random_array = new Uint32Array(1);
        crypto.getRandomValues(random_array);
        
        // get random index
        const chosen_index = random_array[0] % array.length;
        selected_elements.push(array[chosen_index]);

        // remove word from dictionary in O(1)
        const last_index = array.length - 1;
        [array[chosen_index], array[last_index]] = [array[last_index], array[chosen_index]];
        array.pop();

      }

      return selected_elements;

    }

    this.words.set({

      single: getArrayRandomElements(DICTIONARY.single, 35), // it's physically and intellectually impossible to fetch more then 35 words
      double: getArrayRandomElements(DICTIONARY.double, 2)

    });

  }

  // SOUND MANAGEMENT

  public toggleSound(): void{
    this.is_sound_on.update(value => !value);
  }

  // UTILITIES

  public exitBoard(): void{

    const room_id = this.commands.getRoomId();
    this.router.navigate([`/mind-link/waiting-room/${room_id}`]);

  }

  // HOST EVENTS

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent){

    if (this.is_game_active())
      event.returnValue = true;

  }

  // LIFECYCLE

  constructor(
    private router: Router,
    private commands: Commands,
    private events: Events,
    private ng_zone: NgZone,
    private change_detector_ref: ChangeDetectorRef
  ){
    this.sound.load();
  }

  ngOnInit(){

    this.getWords();

    this.roundStartListener();
    this.roundEndListener();
    this.WordPassedListener();
    this.pointsUpdatedListener();
    this.playerLeftListener();

  }

  ngOnDestroy(){
    this.stopTimer();
  }

}
